import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useListDiligentRiskTemplates,
  useListTemplateQuestions,
  useGetRiskMethodology,
  useListCustomRiskModels,
  useGetCustomRiskModel,
  useCreateCustomRiskModel,
  useUpdateCustomRiskModel,
  useDeleteCustomRiskModel,
} from "../../custom-hooks/queries";
import { useDecodedToken } from "../../custom-hooks/useToken";
import { useUserRole, getUserType } from "../../utils/helperMethods";
import {
  computeCriticality,
  resolveConfig,
  aggregateSection,
} from "../RFIS/readonly/riskAggregates";
import NotificationModal from "../../components/NotificationModal";
import {
  naturalCompare,
  anchorTitle,
  Pill,
  AnchoredValueStepper,
  ColHeader,
  TemplateOptions,
  SectionToggleHeader,
} from "../../components/RiskModels/riskModelEditorShared";
import {
  ShieldCheckIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

/**
 * Sponsor authors their own custom risk model: a variant of
 * the Diligent Risk Model for a Diligent template, bound to (template, this
 * sponsor). The sponsor tweaks per-question I/L/D; sections derive their
 * Criticality + Risk Score live; the saved model becomes a selectable option in
 * the Risk Model picker for this sponsor's VQQs of that template.
 *
 * Route: /Sponsor/RiskModels. The sponsor's org is taken from the auth token;
 * the backend re-derives it from the claim and binds the model to that org, so
 * a sponsor can never author for another org. Sponsors can only edit/delete
 * their OWN models — Diligent-admin-authored models are read-only here.
 */

const parseAnchors = (json) => {
  if (!json || typeof json !== "string") return {};
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return {};
    const out = {};
    for (const item of arr) {
      const lvl = Number(item?.level);
      if (Number.isFinite(lvl)) {
        out[lvl] = { level: lvl, anchor: item?.anchor ?? "", definition: item?.definition ?? "" };
      }
    }
    return out;
  } catch {
    return {};
  }
};

// Per-question RAG from the dynamic config bands (mirrors Airtable Question RAG).
const ragForScoreLocal = (score, config) => {
  if (score == null) return null;
  for (const b of config?.question_rag_bands ?? []) {
    const min = b.min ?? 0;
    const max = b.max ?? Infinity;
    if (score >= min && score <= max) return b.band;
  }
  return null;
};

const RAG_STYLES = {
  Red: "bg-red-100 text-red-800 border-red-200",
  Yellow: "bg-amber-100 text-amber-800 border-amber-200",
  Green: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const CRIT_STYLES = {
  Critical: "bg-rose-100 text-rose-800 border-rose-200",
  Major: "bg-orange-100 text-orange-800 border-orange-200",
  Minor: "bg-slate-100 text-slate-700 border-slate-200",
};

/**
 * 1–5 stepper bound to the EFFECTIVE value (override ?? base). Stepping to the
 * base value clears the override; stepping anywhere else sets it. Violet when
 * overridden; the shared stepper renders the anchors and hover copy.
 */
const AnchoredStepper = ({ base, override, anchors, inverted, dimName, onChange }) => {
  const effective = override ?? base ?? null;
  const overridden = override != null && override !== base;
  // Stepping back onto the base value means "no override" (inherit).
  const emit = (n) => onChange(n === base ? null : n);
  return (
    <AnchoredValueStepper
      value={effective}
      dimName={dimName}
      anchors={anchors}
      inverted={inverted}
      highlighted={overridden}
      tone="violet"
      onStep={emit}
    />
  );
};

// ───────────────────────────── Editor ─────────────────────────────

const SponsorModelEditor = ({ templateId, sponsorId, modelId, onSaved, onCancel, onDeleted, onError }) => {
  const { data: existing } = useGetCustomRiskModel(modelId, { enabled: !!modelId });
  const { data: methodology } = useGetRiskMethodology({});
  const { data: questions, isLoading } = useListTemplateQuestions(templateId, { enabled: !!templateId });
  const config = useMemo(() => resolveConfig(methodology), [methodology]);

  const anchorMaps = useMemo(
    () => ({
      Impact: parseAnchors(methodology?.impact_anchors_json),
      Likelihood: parseAnchors(methodology?.likelihood_anchors_json),
      Detectability: parseAnchors(methodology?.detectability_anchors_json),
    }),
    [methodology]
  );

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [overridesById, setOverridesById] = useState({}); // id -> { i, l, d }
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [search, setSearch] = useState("");

  // Seed from the existing model on edit (or reset for a new one).
  useEffect(() => {
    if (!modelId) {
      setName("");
      setDescription("");
      setOverridesById({});
      return;
    }
    if (!existing) return;
    setName(existing.name ?? "");
    setDescription(existing.description ?? "");
    const next = {};
    for (const o of existing.overrides ?? []) {
      next[o.diligent_risk_question_id] = {
        i: o.impact_override ?? null,
        l: o.likelihood_override ?? null,
        d: o.detectability_override ?? null,
      };
    }
    setOverridesById(next);
  }, [existing, modelId]);

  const createMutation = useCreateCustomRiskModel();
  const updateMutation = useUpdateCustomRiskModel();

  const setOverride = useCallback((id, dim, val) => {
    setOverridesById((prev) => {
      const row = { ...(prev[id] || { i: null, l: null, d: null }) };
      row[dim] = val;
      // Drop the row entirely when it has no overrides left.
      if (row.i == null && row.l == null && row.d == null) {
        const { [id]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: row };
    });
  }, []);

  const sorted = useMemo(
    () => [...(questions ?? [])].sort((a, b) => naturalCompare(a.question_code, b.question_code)),
    [questions]
  );
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return sorted;
    return sorted.filter((q) =>
      [q.question_code, q.question_text_snapshot, q.section_name].filter(Boolean).join(" ").toLowerCase().includes(s)
    );
  }, [sorted, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const q of filtered) {
      const key = q.section_name || "Uncategorized";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(q);
    }
    return map;
  }, [filtered]);

  const effective = useCallback(
    (q) => {
      const ov = overridesById[q.diligent_risk_question_id];
      return {
        i: ov?.i ?? q.impact ?? null,
        l: ov?.l ?? q.likelihood ?? null,
        d: ov?.d ?? q.detectability ?? null,
      };
    },
    [overridesById]
  );

  const derive = useCallback(
    (q) => {
      const { i, l, d } = effective(q);
      const rs = i != null && l != null && d != null ? i * l * d : null;
      return { i, l, d, rs, crit: rs != null ? computeCriticality(rs, i, config) : null };
    },
    [effective, config]
  );

  // Live section derivation (Section Criticality + Section Risk Score + RAG),
  // recomputed as overrides change — mirrors the Airtable Sections formulas.
  const sectionAgg = useCallback(
    (qs) => {
      const rows = qs.map((q) => {
        const { i, l, d } = effective(q);
        return { rubric_impact: i, rubric_likelihood: l, rubric_detectability: d };
      });
      return aggregateSection(rows, methodology);
    },
    [effective, methodology]
  );

  const overrideCount = Object.keys(overridesById).length;
  const saving = createMutation.isPending || updateMutation.isPending;

  const toggleSection = useCallback((s) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      onError?.("Name required", "Give your custom risk model a name before saving.");
      return;
    }
    const overrides = Object.entries(overridesById)
      .filter(([, v]) => v && (v.i != null || v.l != null || v.d != null))
      .map(([qid, v]) => ({
        diligent_risk_question_id: qid,
        impact_override: v.i ?? null,
        likelihood_override: v.l ?? null,
        detectability_override: v.d ?? null,
      }));
    const payload = {
      name: name.trim(),
      description: description?.trim() || null,
      diligent_risk_template_id: templateId,
      sponsor_id: sponsorId, // backend re-derives from the claim; sent for parity
      overrides,
    };
    try {
      const res = modelId
        ? await updateMutation.mutateAsync({ customRiskModelId: modelId, payload })
        : await createMutation.mutateAsync(payload);
      if (res?.ok) {
        onSaved?.(res.data, !modelId);
      } else if (res?.conflict) {
        onError?.("Name already used", res.message || "A model with this name already exists for this template.");
      } else {
        onError?.("Couldn't save", res?.message || "Please try again.");
      }
    } catch (err) {
      onError?.(
        "Couldn't save",
        err?.response?.data?.detail || err?.response?.data?.message || err?.message || "Please try again."
      );
    }
  }, [name, description, overridesById, templateId, sponsorId, modelId, createMutation, updateMutation, onSaved, onError]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Header bar */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-white flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{modelId ? "Edit custom model" : "New custom model"}</p>
          <p className="text-[12px] text-gray-500">
            {questions?.length ?? 0} questions
            {overrideCount > 0 ? <span className="text-violet-600 font-medium"> · {overrideCount} overridden</span> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {modelId ? (
            <button
              type="button"
              onClick={() => onDeleted?.(modelId)}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-40"
            >
              <TrashIcon className="h-4 w-4" />
              Delete
            </button>
          ) : null}
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className={`rounded-md px-4 py-1.5 text-sm font-semibold border shadow-sm ${
              saving || !name.trim()
                ? "bg-indigo-300 text-white border-indigo-300 cursor-not-allowed"
                : "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {saving ? "Saving…" : "Save model"}
          </button>
        </div>
      </div>

      {/* Name + description */}
      <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-3 gap-3 border-b border-gray-100">
        <div className="md:col-span-1">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Tighter Weighting"
            maxLength={128}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Description <span className="text-gray-400 normal-case font-normal">(optional)</span>
          </label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What's different about this model?"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Search + hint */}
      <div className="px-4 py-2 flex items-center justify-between gap-4">
        <div className="relative w-64">
          <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search question, section…"
            className="w-full rounded-md border border-gray-300 pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <p className="text-[11px] text-gray-400">
          Regulatory references are inherited from the Diligent Risk Model and aren&apos;t editable here.
        </p>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="px-4 py-10 text-center text-sm text-gray-500">Loading questions…</div>
      ) : (
        <div className="overflow-hidden">
          <div className="grid grid-cols-[1fr_4rem_repeat(3,7rem)_4rem_4.5rem_5rem] gap-2 px-4 py-2 bg-gray-50 border-y border-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            <div>Question</div>
            <div className="text-center">Base</div>
            <div className="text-center"><ColHeader label="Impact" anchors={anchorMaps.Impact} dimName="Impact" /></div>
            <div className="text-center"><ColHeader label="Likelihood" anchors={anchorMaps.Likelihood} dimName="Likelihood" /></div>
            <div className="text-center"><ColHeader label="Detect. (inv)" anchors={anchorMaps.Detectability} dimName="Detectability" inverted /></div>
            <div className="text-center">Risk</div>
            <div className="text-center">RAG</div>
            <div className="text-center">Criticality</div>
          </div>

          {[...grouped.entries()].map(([sectionName, qs]) => {
            const agg = sectionAgg(qs);
            const isCollapsed = collapsed.has(sectionName);
            return (
              <React.Fragment key={sectionName}>
                <SectionToggleHeader
                  sectionName={sectionName}
                  count={qs.length}
                  isCollapsed={isCollapsed}
                  onToggle={() => toggleSection(sectionName)}
                >
                  <span className="ml-auto flex items-center gap-3">
                    <span className="flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wide text-gray-400">Section Risk</span>
                      <span className="text-[12px] font-semibold tabular-nums text-gray-800">
                        {agg?.intrinsic_section_risk_score ?? "—"}
                      </span>
                      <Pill label={agg?.intrinsic_section_risk_rag} styles={RAG_STYLES} />
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wide text-gray-400">Criticality</span>
                      <Pill label={agg?.criticality} styles={CRIT_STYLES} />
                    </span>
                  </span>
                </SectionToggleHeader>

                {!isCollapsed &&
                  qs.map((q) => {
                    const id = q.diligent_risk_question_id;
                    const ov = overridesById[id];
                    const { i, l, d, rs, crit } = derive(q);
                    const isOverridden = !!ov && (ov.i != null || ov.l != null || ov.d != null);
                    return (
                      <div
                        key={id}
                        className={`grid grid-cols-[1fr_4rem_repeat(3,7rem)_4rem_4.5rem_5rem] gap-2 px-4 py-2.5 items-center border-b border-gray-100 last:border-b-0 ${
                          isOverridden ? "bg-violet-50/40" : "hover:bg-gray-50/60"
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[12px] font-semibold text-gray-900">{q.question_code}</span>
                            {isOverridden ? <span className="text-[10px] text-violet-600 font-medium">overridden</span> : null}
                          </div>
                          {q.question_text_snapshot ? (
                            <p className="text-[12px] text-gray-500 truncate" title={q.question_text_snapshot}>
                              {q.question_text_snapshot}
                            </p>
                          ) : null}
                        </div>

                        <div className="text-center text-[11px] text-gray-400 tabular-nums">
                          {q.impact ?? "–"}/{q.likelihood ?? "–"}/{q.detectability ?? "–"}
                        </div>

                        <div className="flex justify-center">
                          <AnchoredStepper base={q.impact} override={ov?.i} anchors={anchorMaps.Impact} dimName="Impact" onChange={(v) => setOverride(id, "i", v)} />
                        </div>
                        <div className="flex justify-center">
                          <AnchoredStepper base={q.likelihood} override={ov?.l} anchors={anchorMaps.Likelihood} dimName="Likelihood" onChange={(v) => setOverride(id, "l", v)} />
                        </div>
                        <div className="flex justify-center">
                          <AnchoredStepper base={q.detectability} override={ov?.d} anchors={anchorMaps.Detectability} dimName="Detectability" inverted onChange={(v) => setOverride(id, "d", v)} />
                        </div>

                        <div className="text-center text-sm font-semibold tabular-nums text-gray-900">{rs ?? "—"}</div>
                        <div className="flex justify-center">
                          <Pill label={rs != null ? ragForScoreLocal(rs, config) : null} styles={RAG_STYLES} />
                        </div>
                        <div className="flex justify-center">
                          <Pill label={crit} styles={CRIT_STYLES} />
                        </div>
                      </div>
                    );
                  })}
              </React.Fragment>
            );
          })}

          {grouped.size === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              {search ? "No questions match your search." : "This template has no questions."}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// ───────────────────────────── Page shell ─────────────────────────────

const SponsorRiskModels = () => {
  const userRole = useUserRole();
  const isSponsor = getUserType(userRole) === "sponsor";
  const { decoded } = useDecodedToken();
  const sponsorId = decoded?.sponsor_id ?? null;

  const [templateId, setTemplateId] = useState("");
  const [editing, setEditing] = useState(null); // { modelId } | { creating:true } | null
  const [notification, setNotification] = useState({ open: false, title: "", desc: "", error: false });
  const [searchParams] = useSearchParams();

  const { data: templates } = useListDiligentRiskTemplates();

  // Deep-link pre-scope: ?template=<diligent_risk_template_id> (from the VQQ
  // Details picker's "manage my models" link) preselects that template once the
  // list confirms it's a real one. Only auto-fills while nothing is picked yet.
  useEffect(() => {
    const pre = searchParams.get("template");
    if (
      pre &&
      !templateId &&
      Array.isArray(templates) &&
      templates.some((t) => t.diligent_risk_template_id === pre)
    ) {
      setTemplateId(pre);
    }
  }, [searchParams, templates, templateId]);
  const { data: models } = useListCustomRiskModels(
    { templateId, sponsorId, includeInactive: false },
    { enabled: !!templateId && !!sponsorId }
  );
  const deleteMutation = useDeleteCustomRiskModel();

  // Only the sponsor's OWN models are editable here. Diligent-admin-authored
  // models for this pair still appear in the VQQ picker but aren't managed here.
  const myModels = useMemo(
    () => (Array.isArray(models) ? models.filter((m) => m.author_kind === "Sponsor") : []),
    [models]
  );

  const notify = useCallback((title, desc, error = false) => {
    setNotification({ open: true, title, desc, error });
  }, []);

  const handleDelete = useCallback(
    async (modelId) => {
      try {
        const ok = await deleteMutation.mutateAsync(modelId);
        if (ok) {
          setEditing(null);
          notify("Model deleted", "It's been removed from the Risk Model picker for future VQQs. Published snapshots that used it are unaffected.");
        } else {
          notify("Couldn't delete", "The model may have already been removed.", true);
        }
      } catch (err) {
        notify("Couldn't delete", err?.response?.data?.detail || err?.message || "Please try again.", true);
      }
    },
    [deleteMutation, notify]
  );

  if (!isSponsor) {
    return (
      <div className="px-6 py-16 text-center text-sm text-gray-500">
        Custom risk model authoring is available to sponsor users only.
      </div>
    );
  }

  return (
    <div className="px-6 py-5 max-w-[1200px] mx-auto">
      {/* Header */}
      <header className="rounded-lg border border-gray-200 bg-white p-5 mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="h-6 w-6 text-indigo-600" />
          <h1 className="text-xl font-semibold text-gray-900">My Custom Risk Models</h1>
        </div>
        <p className="mt-1 text-sm text-gray-500 max-w-3xl">
          Author your own variant of a Diligent template&apos;s Risk Model. Tweak per-question Impact /
          Likelihood / Detectability; sections derive their criticality and risk score automatically. Your
          saved models become selectable on your VQQs of that template.
        </p>
      </header>

      {/* Template picker */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 mb-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Scope</div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[18rem] max-w-2xl">
            <label className="block text-xs font-medium text-gray-600 mb-1">Diligent template</label>
            <select
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setEditing(null);
              }}
              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">— pick a Diligent template —</option>
              <TemplateOptions templates={templates} />
            </select>
          </div>
        </div>
      </div>

      {!templateId ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center text-sm text-gray-500">
          Pick a Diligent template above to author a custom model for it.
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-4">
          {/* Left rail — my models */}
          <aside className="col-span-12 lg:col-span-3 space-y-2">
            <button
              type="button"
              onClick={() => setEditing({ creating: true })}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
            >
              <PlusIcon className="h-4 w-4" />
              New custom model
            </button>
            {myModels.length === 0 ? (
              <p className="text-[12px] text-gray-400 px-1 py-2">No custom models yet for this template.</p>
            ) : (
              myModels.map((m) => {
                const isActive = editing?.modelId === m.custom_risk_model_id;
                return (
                  <button
                    key={m.custom_risk_model_id}
                    type="button"
                    onClick={() => setEditing({ modelId: m.custom_risk_model_id })}
                    className={`w-full text-left rounded-md border px-3 py-2.5 ${
                      isActive ? "border-indigo-400 bg-indigo-50/60" : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <p className="text-[13px] font-semibold text-gray-900 truncate">{m.name}</p>
                    {m.description ? <p className="text-[11px] text-gray-500 truncate">{m.description}</p> : null}
                  </button>
                );
              })
            )}
          </aside>

          {/* Editor */}
          <section className="col-span-12 lg:col-span-9">
            {editing ? (
              <SponsorModelEditor
                key={editing.modelId || "new"}
                templateId={templateId}
                sponsorId={sponsorId}
                modelId={editing.modelId || null}
                onCancel={() => setEditing(null)}
                onSaved={(_data, wasCreate) => {
                  setEditing(null);
                  notify(wasCreate ? "Model created" : "Model saved", "It's available in the Risk Model picker for your VQQs of this template.");
                }}
                onDeleted={handleDelete}
                onError={(title, desc) => notify(title, desc, true)}
              />
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-16 text-center text-sm text-gray-500">
                Select one of your models to edit, or create a new one.
              </div>
            )}
          </section>
        </div>
      )}

      <NotificationModal
        open={notification.open}
        setOpen={(open) => setNotification((prev) => ({ ...prev, open }))}
        title={notification.title}
        desc={notification.desc}
        error={notification.error}
      />
    </div>
  );
};

export default SponsorRiskModels;
